import { Capacitor } from '@capacitor/core';
import { AdMob, AdmobConsentStatus } from '@capacitor-community/admob';

export type AdConsentStatus = 'unknown' | 'accepted' | 'rejected' | 'not_required';

export type AdConsentState = {
  required: boolean;
  status: AdConsentStatus;
  updatedAtMs: number;
};

export type AdConsentProviderInfo = {
  status: AdConsentStatus;
  isRequired: boolean;
  canRequestAds: boolean;
  isConsentFormAvailable: boolean;
  privacyOptionsRequired: boolean;
};

let admobInitStarted = false;
let admobInitPromise: Promise<void> | null = null;

export async function isAdConsentProviderAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    await ensureAdmobInitialized();
    return true;
  } catch {
    return false;
  }
}

export async function refreshAdConsentProviderInfo(): Promise<AdConsentProviderInfo | null> {
  if (!(await isAdConsentProviderAvailable())) return null;
  const info = await AdMob.requestConsentInfo();
  return mapProviderInfo(info.status, info.canRequestAds, Boolean(info.isConsentFormAvailable), info.privacyOptionsRequirementStatus);
}

export async function showAdConsentFormIfRequired(): Promise<AdConsentProviderInfo | null> {
  if (!(await isAdConsentProviderAvailable())) return null;
  const before = await AdMob.requestConsentInfo();
  const mustShow = before.status === AdmobConsentStatus.REQUIRED && before.isConsentFormAvailable;
  const after = mustShow ? await AdMob.showConsentForm() : before;
  return mapProviderInfo(after.status, after.canRequestAds, Boolean(after.isConsentFormAvailable), after.privacyOptionsRequirementStatus);
}

export async function openAdPrivacyOptionsForm(): Promise<boolean> {
  if (!(await isAdConsentProviderAvailable())) return false;
  const info = await AdMob.requestConsentInfo();
  if (info.privacyOptionsRequirementStatus !== 'REQUIRED') return false;
  await AdMob.showPrivacyOptionsForm();
  return true;
}

export function readAdConsentState(raw: string | null, nowMs: number): AdConsentState {
  const fallback: AdConsentState = {
    required: false,
    status: 'not_required',
    updatedAtMs: nowMs
  };
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as Partial<AdConsentState>;
    const updatedAtMs = Number(parsed.updatedAtMs ?? nowMs);
    return {
      required: Boolean(parsed.required),
      status: normalizeStatus(parsed.status),
      updatedAtMs: Number.isFinite(updatedAtMs) ? updatedAtMs : nowMs
    };
  } catch {
    return fallback;
  }
}

export function consentAllowsPersonalizedAds(consent: AdConsentState): boolean {
  return !consent.required || consent.status === 'accepted';
}

function normalizeStatus(status: unknown): AdConsentStatus {
  if (status === 'accepted' || status === 'rejected' || status === 'not_required') return status;
  return 'unknown';
}

async function ensureAdmobInitialized(): Promise<void> {
  if (admobInitStarted && admobInitPromise) return admobInitPromise;
  admobInitStarted = true;
  admobInitPromise = AdMob.initialize({
    initializeForTesting: import.meta.env.DEV
  });
  return admobInitPromise;
}

function mapProviderInfo(
  providerStatus: AdmobConsentStatus,
  canRequestAds: boolean,
  isConsentFormAvailable: boolean,
  privacyOptionsStatus: string
): AdConsentProviderInfo {
  let status: AdConsentStatus = 'unknown';
  if (providerStatus === AdmobConsentStatus.NOT_REQUIRED) status = 'not_required';
  if (providerStatus === AdmobConsentStatus.OBTAINED) status = 'accepted';
  return {
    status,
    isRequired: providerStatus === AdmobConsentStatus.REQUIRED,
    canRequestAds,
    isConsentFormAvailable,
    privacyOptionsRequired: privacyOptionsStatus === 'REQUIRED'
  };
}
